import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Container,
  Typography,
} from "@mui/material";
import {
  ApiOutlined,
  AppsOutlined,
  SupportAgentOutlined,
} from "@mui/icons-material";
import AgentChatMessageBubble from "../components/common/AgentChatMessageBubble";
import ConciergeConversationPanel, {
  CONCIERGE_CHAT_HEIGHT,
} from "../components/home/ConciergeConversationPanel";
import ConciergeIntentForm from "../components/home/ConciergeIntentForm";
import GuidedJourneyPanel from "../components/home/GuidedJourneyPanel";
import HomeQuickLinks, {
  type HomeQuickLinkItem,
} from "../components/home/HomeQuickLinks";
import { useActiveJourney } from "../contexts/ActiveJourneyContext";
import { useAuth } from "../contexts/AuthContext";
import { useActiveWorkspace } from "../hooks/useActiveWorkspace";

const logoSx = {
  margin: 0,
  letterSpacing: "-0.03em",
  fontWeight: 500,
  background: "linear-gradient(74deg, #34a853, #ea4335, #fbbc04, #4285f4)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  lineHeight: 1.1,
} as const;

const RESULTS_WORKSPACE_MAX_WIDTH = 1120;
const SIDEBAR_WIDTH = 360;

const HomePage: React.FC = () => {
  const { token } = useAuth();
  const { available: workspaces } = useActiveWorkspace();
  const isLoggedIn = Boolean(token);

  const journeyState = useActiveJourney();
  const [intent, setIntent] = useState(journeyState.userIntent);

  useEffect(() => {
    if (journeyState.userIntent) {
      setIntent(journeyState.userIntent);
    }
  }, [journeyState.userIntent]);

  const showResults =
    journeyState.hasActiveJourney ||
    journeyState.messages.some((m) => m.role === "user");

  const handleSubmitIntent = async () => {
    if (!intent.trim()) return;
    await journeyState.submitIntent(intent.trim());
  };

  const handleStartOver = () => {
    journeyState.resetJourney();
    setIntent("");
  };

  const quickLinks: HomeQuickLinkItem[] = [
    {
      label: "Products",
      path: "/product-explorer",
      icon: AppsOutlined,
    },
    {
      label: "APIs",
      path: "/api-explorer",
      icon: ApiOutlined,
    },
    {
      label: "Support",
      path: "/support-cases",
      icon: SupportAgentOutlined,
    },
    ...workspaces
      .filter((ws) => ws.id !== "developer")
      .map((ws) => ({
        label: ws.label,
        path: ws.homePath,
        icon: ws.icon,
      })),
  ].filter(() => isLoggedIn);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        ...(showResults
          ? {
              height: { xs: "calc(100dvh - 64px)", md: "100dvh" },
              maxHeight: { xs: "calc(100dvh - 64px)", md: "100dvh" },
              overflow: "hidden",
            }
          : { overflow: "visible" }),
        bgcolor: "background.default",
      }}
    >
      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          px: { xs: 2, md: 3 },
          py: showResults ? { xs: 2, md: 2.5 } : { xs: 4, md: 6 },
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            width: "100%",
            flex: showResults ? 1 : undefined,
            display: "flex",
            flexDirection: "column",
            minHeight: showResults ? 0 : undefined,
            maxWidth: showResults
              ? { xs: "100%", md: RESULTS_WORKSPACE_MAX_WIDTH }
              : { xs: "100%", md: 680 },
            mx: "auto",
            ...(showResults
              ? {}
              : {
                  alignItems: "center",
                  textAlign: "center",
                }),
          }}
        >
          <Box
            sx={{
              width: "100%",
              flex: showResults ? 1 : undefined,
              display: "flex",
              flexDirection: "column",
              minHeight: showResults ? 0 : undefined,
              ...(showResults ? {} : { maxWidth: 560, mx: "auto" }),
            }}
          >
            {showResults ? (
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "baseline",
                  gap: 1.5,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    ...logoSx,
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  }}
                >
                  SAMi
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Concierge session
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mb: 0 }}>
                <Box
                  component="h1"
                  sx={{
                    ...logoSx,
                    fontSize: { xs: "3rem", sm: "4rem" },
                  }}
                >
                  SAMi
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 1, fontSize: "1.05rem" }}
                >
                  Smart API Marketplace
                </Typography>
              </Box>
            )}

            {!showResults && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    mt: { xs: 3, md: 4 },
                    fontWeight: 400,
                    maxWidth: 480,
                    mx: "auto",
                  }}
                >
                  What would you like to build or integrate?
                </Typography>
                <Box sx={{ width: "100%", mt: { xs: 3, md: 4 } }}>
                  <ConciergeIntentForm
                    intent={intent}
                    onIntentChange={setIntent}
                    onSubmit={handleSubmitIntent}
                    loading={journeyState.loading}
                    disabled={!journeyState.sessionReady}
                  />
                </Box>
              </>
            )}

            {journeyState.error && (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  width: "100%",
                  textAlign: "left",
                  borderRadius: 2,
                }}
              >
                {journeyState.error}
              </Alert>
            )}

            {journeyState.loading && !showResults && (
              <Box
                sx={{
                  mt: 4,
                  width: "100%",
                  textAlign: "left",
                  maxWidth: 560,
                  mx: "auto",
                }}
              >
                <AgentChatMessageBubble
                  role="assistant"
                  content=""
                  isStreaming
                  structuredBlockPreset="concierge"
                />
              </Box>
            )}

            {showResults && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "stretch",
                  gap: { xs: 2.5, md: 3 },
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <ConciergeConversationPanel
                    messages={journeyState.messages}
                    loading={journeyState.loading}
                    awaitingConfirmation={journeyState.awaitingJourneyConfirmation}
                    sessionReady={journeyState.sessionReady}
                    onSend={(text) => void journeyState.sendMessage(text)}
                    onStartOver={handleStartOver}
                  />

                  {journeyState.journey && (
                    <Box sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
                      <GuidedJourneyPanel
                        journey={journeyState.journey}
                        currentStepIndex={journeyState.currentStepIndex}
                        completedStepIds={journeyState.completedStepIds}
                      />
                    </Box>
                  )}

                  <HomeQuickLinks links={quickLinks} align="start" />
                </Box>

                {journeyState.journey && (
                  <Box
                    sx={{
                      display: { xs: "none", md: "block" },
                      width: SIDEBAR_WIDTH,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        height: CONCIERGE_CHAT_HEIGHT,
                        maxHeight: CONCIERGE_CHAT_HEIGHT,
                        overflowY: "auto",
                        pr: 0.5,
                      }}
                    >
                      <GuidedJourneyPanel
                        journey={journeyState.journey}
                        currentStepIndex={journeyState.currentStepIndex}
                        completedStepIds={journeyState.completedStepIds}
                        variant="sidebar"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {!showResults && <HomeQuickLinks links={quickLinks} align="center" />}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
